import type { RbacController } from '@twinepos/vue-rbac'

/** How long to coalesce a burst of 403s into a single abilities refetch. */
const DEFAULT_COALESCE_MS = 2_000

export interface ForbiddenRevalidatorOptions {
  /**
   * Statuses that mean "the backend disagrees with the abilities we hold".
   * 403 is the real boundary being crossed. 401 is included because a session
   * that has been re-authenticated as somebody else must not keep the previous
   * user's affordances.
   */
  statuses?: number[]
  /** Burst window (ms). A page firing ten parallel requests should refetch once. */
  coalesceMs?: number
}

/**
 * Builds a response observer that refetches abilities when the backend refuses a
 * request the UI believed was allowed.
 *
 * This is the drift-detection half of the contract, and it works precisely
 * because `version` doubles as the ETag: the refetch sends `If-None-Match` and
 * costs a 304 when nothing changed, so reacting to every 403 is cheap. A role
 * revocation, a permission removed from a role, an impersonation — all surface
 * the same way, through the boundary that was going to reject the request
 * anyway. No special header, and no server-side cache to invalidate.
 *
 * The frontend deliberately does not poll for role changes. A stale affordance
 * costs one rejected click; the 403 that rejects it is what corrects the UI.
 *
 * Attach the returned function to the app's HTTP client response path.
 */
export const createForbiddenRevalidator = (
  controller: RbacController,
  options: ForbiddenRevalidatorOptions = {}
): ((response: { status: number }) => void) => {
  const statuses = options.statuses ?? [401, 403]
  const coalesceMs = options.coalesceMs ?? DEFAULT_COALESCE_MS

  let lastRefetchAt: number | null = null

  return (response: { status: number }): void => {
    if (!statuses.includes(response.status)) {
      return
    }

    // Nothing to correct before a first payload has landed; the initial fetch is
    // already in flight and will bring the truth with it.
    if (controller.version.value === null) {
      return
    }

    const now = Date.now()

    if (lastRefetchAt !== null && now - lastRefetchAt < coalesceMs) {
      return
    }

    lastRefetchAt = now
    void controller.refresh()
  }
}
